import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import {
  Checkbox,
  CHECKBOX_LABEL_POSITIONS,
  CHECKBOX_SIZES,
  IconButton,
  SimpleTable,
  SimpleTableBody,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableData,
  IconExpandMore,
  transitionAnimation,
} from "cdk-radial";

function useDelayUnmount(isMounted, delayTime) {
  const [showDiv, setShowDiv] = useState(false);
  useEffect(() => {
    let timeoutId;
    if (isMounted && !showDiv) {
      setShowDiv(true);
    } else if (!isMounted && showDiv) {
      timeoutId = setTimeout(() => setShowDiv(false), delayTime);
    }
    return () => clearTimeout(timeoutId);
  }, [isMounted, delayTime, showDiv]);
  return showDiv;
}

const StyledDiv = styled.div`
  overflow: hidden;
  ${({ isOpen }) =>
    isOpen
      ? css`
          animation: inAnimation 300ms ease-in;
        `
      : css`
          animation: outAnimation 300ms ease-out;
          animation-fill-mode: forwards;
        `}

  @keyframes inAnimation {
    0% {
      max-height: 0px;
    }
    100% {
      max-height: 500px; // max-heght can be adjusted by product team depending on their needs
    }
  }

  @keyframes outAnimation {
    0% {
      max-height: 500px; // max-heght can be adjusted by product team depending on their needs
    }
    100% {
      max-height: 0px;
    }
  }
`;

const ZebraSimpleTableRow = styled(SimpleTableRow)`
  background-color: ${({ theme }) => theme.color.zebraStripe.background.value};
  border: 1px solid ${({ theme }) => theme.color.gray[300].value};

  &:last-child {
    border-bottom: none;
  }
`;

const EntitlementsTableHeader = styled(SimpleTableHeader)`
  background-color: ${({ theme }) => theme.color.zebraStripe.background.value};
  border-top: 1px solid ${({ theme }) => theme.color.gray[300].value};
  padding: 1rem;

  font-family: "Montserrat", sans-serif;
  font-weight: 600;
  color: #151b25b3;

  box-shadow: inset 0 -1px 0 #151b25b3;
`;

const grayText = "#151B25B3";

const RoleTableRow = styled(SimpleTableRow)`
  background-color: white;
`;
const RoleTableData = styled(SimpleTableData)`
  font-family: "Montserrat", sans-serif;
  font-weight: 600;
  color: #151b25;
  padding: 1rem 0rem;
`;

const EntitlementsTableData = styled(SimpleTableData)`
  font-family: "Montserrat", sans-serif;
  color: ${grayText};
  padding: 1rem;
`;

const StyledIconButton = styled(IconButton)`
  ${transitionAnimation("transform")}
  flex-shrink: 0;
  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: rotate(-180deg);
    `}
`;

const EntitlementData = styled(SimpleTableData)`
  padding: 0px;
`;

const RoleRow = ({
  role,
  selectedRolesList = [],
  handleCheckChange = () => {},
  canAssignRoles = false,
  globalDisable = false,
  hasCheckboxes = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMounted = useDelayUnmount(isOpen, 300);

  const isSelected = selectedRolesList.some((r) => r.id === role.id);

  const handleToggle = () => {
    setIsOpen((isOpen) => !isOpen);
  };

  return (
    <>
      <RoleTableRow>
        <SimpleTableData style={{ width: "1%", padding: "0 8px" }}>
          {hasCheckboxes && <Checkbox
            dataTestId={`role-${role.id}-checkbox`}
            checked={isSelected}
            id={`role-${role.id}-checkbox`}
            labelPosition={CHECKBOX_LABEL_POSITIONS.AFTER}
            label={role.name}
            hideLabel={true}
            name="role-checkbox"
            onChange={() => handleCheckChange(role)}
            size={CHECKBOX_SIZES.STANDARD}
            isDisabled={!canAssignRoles || globalDisable}
          />}
        </SimpleTableData>
        <RoleTableData>{role.name}</RoleTableData>
        <SimpleTableData style={{ width: "1%" }}>
          <StyledIconButton
            icon={<IconExpandMore />}
            onClick={handleToggle}
            text={isOpen ? "Collapse" : "Expand"}
            isOpen={isOpen}
            dataTestId={`expand-role-${role.id}`}
            id={`expand-role-${role.id}`}
            name="expand-role-button"
          />
        </SimpleTableData>
      </RoleTableRow>
      {isMounted && (
        <SimpleTableRow hideStateVisibility={true}>
          <EntitlementData colSpan={3}>
            <StyledDiv isOpen={isOpen}>
              <SimpleTable aria-label="Detail Table" style={{ width: "100%" }}>
                <SimpleTableHead>
                  <SimpleTableRow>
                    <EntitlementsTableHeader>
                      Included Entitlements
                    </EntitlementsTableHeader>
                  </SimpleTableRow>
                </SimpleTableHead>
                <SimpleTableBody>
                  {role.permissions?.map((entitlement, i) => (
                    <ZebraSimpleTableRow
                      hideStateVisibility={true}
                      key={`entitlement-${i}`}
                    >
                      <EntitlementsTableData>
                        {entitlement.name}
                      </EntitlementsTableData>
                    </ZebraSimpleTableRow>
                  ))}
                </SimpleTableBody>
              </SimpleTable>
            </StyledDiv>
          </EntitlementData>
        </SimpleTableRow>
      )}
    </>
  );
};

export default RoleRow;
